import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { IonIcon } from '@ionic/react';
import { ellipsisHorizontal } from 'ionicons/icons';
import { ActionAlert } from '../../atoms';
import './DataTable.css';

const DataTable = ({ 
  data = [], 
  columns = [],
  loading = false,
  emptyState = {},
  onRowClick,
  actions = [],
  rowKey = 'id',
  selectedRowKey = null,
}) => {
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const triggerRefs = useRef({});

  const updateMenuPosition = useCallback((itemId) => {
    const trigger = triggerRefs.current[itemId];
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        left: rect.right - 160,
      });
    }
  }, []);

  useEffect(() => {
    if (!openMenuId) return;
    const handleClickOutside = (e) => {
      const trigger = triggerRefs.current[openMenuId];
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        (!trigger || !trigger.contains(e.target))
      ) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const toggleMenu = (e, itemId) => {
    e.stopPropagation();
    if (openMenuId === itemId) {
      setOpenMenuId(null);
    } else {
      updateMenuPosition(itemId);
      setOpenMenuId(itemId);
    }
  };

  // Shared column-aware skeleton — used for BOTH the loading state (here) and
  // the empty state below. DataTable is the one list component the pipelines
  // AND apps pages render, so this single skeleton serves both. No spinner.
  //
  // The skeleton MIRRORS the real cell shape per column: a column opts in via
  // `col.skeleton` so a name-with-icon cell shows an icon block + text bar, a
  // status cell shows a dot + short bar, etc. Without it a column falls back to
  // a plain bar. Widths are deterministic (seeded off row+col index) so the
  // skeleton doesn't jitter between renders.
  const skeletonWidth = (rowIdx, colIdx) => {
    const variants = [62, 74, 55, 68];
    return variants[(rowIdx + colIdx) % variants.length];
  };

  const renderSkeletonCell = (col, rowIdx, colIdx) => {
    switch (col.skeleton) {
      case 'avatar':
        // Icon-leading cell (e.g. NAME): square block + text bar.
        return (
          <div className="skeleton-avatar">
            <div className="skeleton-square"></div>
            <div className="skeleton-bar" style={{ width: '46%' }}></div>
          </div>
        );
      case 'status':
        // Dot + label cell (e.g. STATUS): round dot + short bar.
        return (
          <div className="skeleton-status">
            <div className="skeleton-dot"></div>
            <div className="skeleton-bar" style={{ width: '56px' }}></div>
          </div>
        );
      case 'short':
        // Short tokens (e.g. VERSION): a narrow bar.
        return <div className="skeleton-bar" style={{ width: '36px' }}></div>;
      default:
        return <div className="skeleton-bar" style={{ width: `${skeletonWidth(rowIdx, colIdx)}%` }}></div>;
    }
  };

  const renderSkeletonTable = () => (
    <table className="data-table">
      <colgroup>
        {columns.map((col, idx) => (
          <col key={idx} style={col.width ? { width: col.width } : undefined} />
        ))}
        {actions.length > 0 && <col className="data-table-actions-col" />}
      </colgroup>
      <thead className="data-table-skeleton-header">
        <tr>
          {columns.map((col, idx) => (
            <th key={idx}>
              <div className="skeleton-bar" style={{ width: '40%', height: '10px' }}></div>
            </th>
          ))}
          {actions.length > 0 && <th className="data-table-actions-th"></th>}
        </tr>
      </thead>
      <tbody>
        {[0, 1, 2, 3, 4].map((rowIdx) => (
          <tr key={rowIdx} className="data-table-row data-table-skeleton-row">
            {columns.map((col, colIdx) => (
              <td key={colIdx} className={col.className || ''}>
                {renderSkeletonCell(col, rowIdx, colIdx)}
              </td>
            ))}
            {actions.length > 0 && <td className="data-table-actions"></td>}
          </tr>
        ))}
      </tbody>
    </table>
  );

  if (loading) {
    // Skeleton instead of a spinner. Same wrapper as the populated table so
    // there's no layout shift when the real rows land.
    return (
      <div className="data-table-wrapper page-fade-in main-body">
        {renderSkeletonTable()}
      </div>
    );
  }

  if (data.length === 0) {
    // emptyState.content (a React node) overrides the default ActionAlert.
    // Lets callers drop in a richer empty state (e.g. an animated demo)
    // without touching DataTable's surrounding skeleton/wrapper.
    return (
      <div className="data-table-wrapper data-table-has-empty empty page-fade-in main-body">
        <div className="data-table-empty">
          {emptyState.content || (
            <ActionAlert
              title={emptyState.title || "No Data"}
              description={emptyState.description || "No items to display."}
              actionLabel={emptyState.actionLabel}
              onAction={emptyState.onAction}
              buttonRef={emptyState.buttonRef}
            />
          )}
        </div>
        {/* Same shared skeleton as the loading state (renderSkeletonTable). */}
        {renderSkeletonTable()}
      </div>
    );
  }

  const openItem = openMenuId ? data.find(item => item[rowKey] === openMenuId) : null;

  return (
    <div className="data-table-wrapper page-fade-in main-body">
      <table className="data-table">
        {/* The table is table-layout:fixed, so column widths come from here,
            not from content. A column opts in via `col.width` (e.g. '40%' or
            '320px'); columns without it share the remaining space equally —
            so existing tables are unaffected. */}
        <colgroup>
          {columns.map((col, idx) => (
            <col key={idx} style={col.width ? { width: col.width } : undefined} />
          ))}
          {actions.length > 0 && <col className="data-table-actions-col" />}
        </colgroup>
        <thead>
          <tr>
            {columns.map((col, idx) => (
              <th key={idx}>{col.header}</th>
            ))}
            {actions.length > 0 && <th className="data-table-actions-th"></th>}
          </tr>
        </thead>
        <tbody>
          {data.map((item) => (
            <tr
              key={item[rowKey]}
              className={`data-table-row${selectedRowKey && item[rowKey] === selectedRowKey ? ' data-table-row--selected' : ''}`}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
            >
              {columns.map((col, idx) => (
                <td 
                  key={idx} 
                  className={col.className || ''}
                  onClick={col.onClick ? (e) => {
                    e.stopPropagation();
                    col.onClick(item, e);
                  } : undefined}
                >
                  {col.render ? col.render(item) : item[col.key]}
                </td>
              ))}
              {actions.length > 0 && (
                <td className="data-table-actions">
                  <button
                    className="data-action-trigger"
                    ref={(el) => { triggerRefs.current[item[rowKey]] = el; }}
                    onClick={(e) => toggleMenu(e, item[rowKey])}
                  >
                    <IonIcon icon={ellipsisHorizontal} />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {openMenuId && openItem && createPortal(
        <div
          className="data-action-dropdown"
          ref={menuRef}
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          {actions.map((action, idx) => {
            // Per-row disabled support: action.disabled may be a static
            // bool OR a function(item) → bool, evaluated against the
            // currently-open row. action.disabledTitle is the tooltip
            // shown when disabled — same shape (string or function).
            const isDisabled = typeof action.disabled === 'function'
              ? !!action.disabled(openItem)
              : !!action.disabled;
            const disabledTitle = isDisabled
              ? (typeof action.disabledTitle === 'function'
                  ? action.disabledTitle(openItem)
                  : action.disabledTitle)
              : undefined;
            // Label may be a static string OR a function(item) → string, so
            // an action can read off the open row (e.g. Pin vs Unpin). Same
            // shape as `disabled`/`disabledTitle` above.
            const label = typeof action.label === 'function'
              ? action.label(openItem)
              : action.label;
            return (
              <React.Fragment key={idx}>
                {action.divider ? (
                  <div className="data-dropdown-divider" />
                ) : (
                  <button
                    className={`data-dropdown-item ${action.danger ? 'data-dropdown-item-danger' : ''} ${isDisabled ? 'data-dropdown-item-disabled' : ''}`}
                    disabled={isDisabled}
                    title={disabledTitle}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isDisabled) return;
                      action.onClick(openItem);
                      setOpenMenuId(null);
                    }}
                  >
                    {action.icon && <IonIcon icon={action.icon} />}
                    <span>{label}</span>
                  </button>
                )}
              </React.Fragment>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

export default DataTable;
